/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  TextField,
  Button,
  Grid,
  InputAdornment,
  Popover,
  Typography,
  Divider,
  IconButton,
  Autocomplete,
  Popper,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Add,
  Remove,
  ChildCare,
} from '@mui/icons-material';

// ============================================================================
// STEPPER INPUT COMPONENT
// ============================================================================

interface StepperInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  label: string;
  sublabel?: string;
  icon?: React.ReactNode;
}

const StepperInput: React.FC<StepperInputProps> = ({
  value,
  onChange,
  min = 0,
  max = 10,
  label,
  sublabel,
  icon,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      py: 1.5,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        {sublabel && (
          <Typography variant="caption" color="text.secondary">
            {sublabel}
          </Typography>
        )}
      </Box>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <IconButton
        size="small"
        onClick={() => value > min && onChange(value - 1)}
        disabled={value <= min}
        sx={{
          border: '1px solid',
          borderColor: value <= min ? '#e0e0e0' : '#2D6E6A',
          color: value <= min ? '#e0e0e0' : '#2D6E6A',
          width: 32,
          height: 32,
        }}
      >
        <Remove fontSize="small" />
      </IconButton>
      <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>
        {value}
      </Typography>
      <IconButton
        size="small"
        onClick={() => value < max && onChange(value + 1)}
        disabled={value >= max}
        sx={{
          border: '1px solid',
          borderColor: value >= max ? '#e0e0e0' : '#2D6E6A',
          color: value >= max ? '#e0e0e0' : '#2D6E6A',
          width: 32,
          height: 32,
        }}
      >
        <Add fontSize="small" />
      </IconButton>
    </Box>
  </Box>
);

// ============================================================================
// TYPES
// ============================================================================

interface ActivitySearchFormProps {
  onSearch?: (params: ActivitySearchParams) => void;
}

interface ActivitySearchParams {
  destination: string;
  date: string;
  adults: number;
  children: number;
  keyword: string;
}

// ============================================================================
// DESTINATION DATA & AUTOCOMPLETE
// ============================================================================

interface DestinationOption {
  name: string;
  region: string;
  country: string;
  type: 'city' | 'region' | 'landmark';
  label: string;
}

const DESTINATIONS: DestinationOption[] = [
  { name: 'New York', region: 'New York', country: 'US', type: 'city', label: 'New York, NY, USA' },
  { name: 'Los Angeles', region: 'California', country: 'US', type: 'city', label: 'Los Angeles, CA, USA' },
  { name: 'San Francisco', region: 'California', country: 'US', type: 'city', label: 'San Francisco, CA, USA' },
  { name: 'Chicago', region: 'Illinois', country: 'US', type: 'city', label: 'Chicago, IL, USA' },
  { name: 'Miami', region: 'Florida', country: 'US', type: 'city', label: 'Miami, FL, USA' },
  { name: 'Orlando', region: 'Florida', country: 'US', type: 'city', label: 'Orlando, FL, USA' },
  { name: 'Las Vegas', region: 'Nevada', country: 'US', type: 'city', label: 'Las Vegas, NV, USA' },
  { name: 'Honolulu', region: 'Hawaii', country: 'US', type: 'city', label: 'Honolulu, HI, USA' },
  { name: 'London', region: 'England', country: 'UK', type: 'city', label: 'London, England, UK' },
  { name: 'Paris', region: 'Île-de-France', country: 'France', type: 'city', label: 'Paris, France' },
  { name: 'Rome', region: 'Lazio', country: 'Italy', type: 'city', label: 'Rome, Italy' },
  { name: 'Barcelona', region: 'Catalonia', country: 'Spain', type: 'city', label: 'Barcelona, Spain' },
  { name: 'Amsterdam', region: 'North Holland', country: 'Netherlands', type: 'city', label: 'Amsterdam, Netherlands' },
  { name: 'Tokyo', region: 'Kanto', country: 'Japan', type: 'city', label: 'Tokyo, Japan' },
  { name: 'Kyoto', region: 'Kansai', country: 'Japan', type: 'city', label: 'Kyoto, Japan' },
  { name: 'Bangkok', region: 'Central Thailand', country: 'Thailand', type: 'city', label: 'Bangkok, Thailand' },
  { name: 'Singapore', region: 'Singapore', country: 'Singapore', type: 'city', label: 'Singapore' },
  { name: 'Dubai', region: 'Dubai', country: 'UAE', type: 'city', label: 'Dubai, UAE' },
  { name: 'Cancún', region: 'Quintana Roo', country: 'Mexico', type: 'city', label: 'Cancún, Mexico' },
  { name: 'Grand Canyon', region: 'Arizona', country: 'US', type: 'landmark', label: 'Grand Canyon, AZ, USA' },
  { name: 'Niagara Falls', region: 'New York', country: 'US', type: 'landmark', label: 'Niagara Falls, NY, USA' },
];

const filterDestinations = (query: string): DestinationOption[] => {
  if (!query || query.length < 2) return [];
  const normalizedQuery = query.toLowerCase().trim();
  return DESTINATIONS.filter(dest => {
    const searchFields = [dest.name.toLowerCase(), dest.region.toLowerCase(), dest.label.toLowerCase()];
    return searchFields.some(field => field.includes(normalizedQuery));
  }).slice(0, 8);
};

const AutocompletePopper = (props: any) => (
  <Popper {...props} placement="bottom-start" style={{ zIndex: 1500 }} />
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ActivitySearchForm: React.FC<ActivitySearchFormProps> = ({ onSearch }) => {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];

  const [formData, setFormData] = useState<ActivitySearchParams>({
    destination: '',
    date: today,
    adults: 2,
    children: 0,
    keyword: '',
  });

// Traveler selector popover
const [travelerAnchor, setTravelerAnchor] = useState<HTMLElement | null>(null);
const [dateError, setDateError] = useState('');

const handleChange = (field: keyof ActivitySearchParams) => (
  e: React.ChangeEvent<HTMLInputElement>
) => {
  setDateError('');
  setFormData({ ...formData, [field]: e.target.value });
};

const handleSearch = () => {
  if (!formData.destination.trim()) {
    setDateError('Please enter a destination');
    return;
  }
  if (formData.date < today) {
    setDateError('Activity date cannot be in the past');
    return;
  }
  setDateError('');
  if (onSearch) {
      onSearch(formData);
    } else {
      const params = new URLSearchParams({
        destination: formData.destination,
        date: formData.date,
        adults: formData.adults.toString(),
        children: formData.children.toString(),
        ...(formData.keyword && { keyword: formData.keyword }),
      });
      navigate(`/search/activities?${params.toString()}`);
    }
  };

  // Traveler summary text
  const totalTravelers = formData.adults + formData.children;
  const travelerSummary = `${formData.adults} adult${formData.adults > 1 ? 's' : ''}${formData.children > 0 ? `, ${formData.children} child${formData.children > 1 ? 'ren' : ''}` : ''}`;

  return (
    <Box>
      {dateError && (
        <Alert severity="error" onClose={() => setDateError('')} sx={{ mb: 1.5, borderRadius: 2 }}>
          {dateError}
        </Alert>
      )}
      <Grid container spacing={2} alignItems="flex-end">
       {/* Destination */}
       <Grid item xs={12} md={3}>
          <Autocomplete
            freeSolo
            options={filterDestinations(formData.destination)}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
            inputValue={formData.destination}
            onInputChange={(_, newValue) => setFormData({ ...formData, destination: newValue })}
            onChange={(_, newValue) => {
              if (newValue && typeof newValue !== 'string') {
                setFormData({ ...formData, destination: newValue.label });
              }
            }}
            PopperComponent={AutocompletePopper}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Box sx={{ 
                    bgcolor: option.type === 'city' ? '#E8F5E9' : option.type === 'landmark' ? '#FFF3E0' : '#E3F2FD',
                    color: option.type === 'city' ? '#2E7D32' : option.type === 'landmark' ? '#E65100' : '#1976D2',
                    px: 0.75, py: 0.25, borderRadius: 1, fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase',
                  }}>
                    {option.type}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.region}, {option.country}</Typography>
                  </Box>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Destination"
                placeholder="City or region"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <LocationIcon color="primary" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}
          />
        </Grid>

        {/* Date */}
        <Grid item xs={6} md={2}>
          <TextField
            fullWidth
            label="Date"
            type="date"
            value={formData.date}
            onChange={handleChange('date')}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: today }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Grid>

        {/* Travelers */}
        <Grid item xs={6} md={2}>
          <TextField
            fullWidth
            label="Travelers"
            value={travelerSummary}
            onClick={(e) => setTravelerAnchor(e.currentTarget)}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                cursor: 'pointer',
              },
              '& .MuiOutlinedInput-input': {
                cursor: 'pointer',
              },
            }}
          />

          {/* Traveler Selector Popover */}
          <Popover
            open={Boolean(travelerAnchor)}
            anchorEl={travelerAnchor}
            onClose={() => setTravelerAnchor(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                p: 2,
                width: 280,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              },
            }}
          >
            <StepperInput
              value={formData.adults}
              onChange={(val) => setFormData({ ...formData, adults: val })}
              min={1}
              max={15}
              label="Adults"
              sublabel="Age 13+"
              icon={<PersonIcon sx={{ color: '#666' }} />}
            />
            <Divider sx={{ my: 1 }} />
            <StepperInput
              value={formData.children}
              onChange={(val) => setFormData({ ...formData, children: val })}
              min={0}
              max={10}
              label="Children"
              sublabel="Age 3-12"
              icon={<ChildCare sx={{ color: '#666' }} />}
            />

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Children under 3 are usually free
            </Typography>

            <Button
              fullWidth
              variant="contained"
              onClick={() => setTravelerAnchor(null)}
              sx={{
                mt: 2,
                bgcolor: '#2D6E6A',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: '#009688' },
              }}
            >
              Done
            </Button>
          </Popover>
        </Grid>

        {/* Keyword (optional) */}
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="What are you looking for? (optional)"
            placeholder="Tours, museums, adventures..."
            value={formData.keyword}
            onChange={handleChange('keyword')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Grid>

        {/* Search Button */}
        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSearch}
            disabled={!formData.destination}
            startIcon={<SearchIcon />}
            sx={{
              py: 1.8,
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            Search
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ActivitySearchForm;
